import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortablePickingLineItem } from './SortablePickingLineItem';
import { PickingLineItem } from './PickingLineItem';
import { PalletSummaryDto } from '../../../../types/picking';
import { PickingLine } from './types';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface PickingLinesListProps {
  lines: PickingLine[];
  currentLineIndex: number | null;
  activeLineIndex: number | null;
  pallets: PalletSummaryDto[];
  editingLocation: number | null;
  editingQuantity: number | null;
  newLocation: string;
  newQuantity: string;
  showPalletInfo: string | null;
  onLineClick: (index: number) => void;
  onLocationEdit: (lineNo: number, location: string) => void;
  onQuantityEdit: (lineNo: number, quantity: number) => void;
  onStartLocationEdit: (lineNo: number, location: string) => void;
  onStartQuantityEdit: (lineNo: number, quantity: string) => void;
  onLocationChange: (value: string) => void;
  onQuantityChange: (value: string) => void;
  onSplitLine: (lineNo: number) => void;
  onPickLine: (lineNo: number, qty: number) => void;
  onUndoPicking: (lineNo: number) => void;
  onShowPalletInfo: (palletId: string | null) => void;
  onReorder: (oldIndex: number, newIndex: number) => void;
  onMoveToEnd?: (lineNo: number) => void;
  enableDragAndDrop: boolean;
}

export const PickingLinesList: React.FC<PickingLinesListProps> = ({
  lines,
  currentLineIndex,
  activeLineIndex,
  pallets,
  editingLocation,
  editingQuantity,
  newLocation,
  newQuantity,
  showPalletInfo,
  onLineClick,
  onLocationEdit,
  onQuantityEdit,
  onStartLocationEdit,
  onStartQuantityEdit,
  onLocationChange,
  onQuantityChange,
  onSplitLine,
  onPickLine,
  onUndoPicking,
  onShowPalletInfo,
  onReorder,
  onMoveToEnd,
  enableDragAndDrop
}) => {
  const { t } = useTranslation();
  // Manko-seksjon (Ariens): linjene er allerede sortert med [X]-manko nederst (usePickingData).
  // Vis en overskrift rett før første mankolinje.
  const firstShortageIndex = lines.findIndex((l) => l.binCode === '[X]');
  const shortageCount = lines.filter((l) => l.binCode === '[X]').length;
  const shortageHeader = (index: number) =>
    index === firstShortageIndex && firstShortageIndex !== -1 ? (
      <div className="flex items-center gap-1.5 pt-3 mt-1 border-t border-amber-200 text-xs font-semibold text-amber-700">
        <AlertTriangle size={13} />
        {t('warehouse.modules.picking.sections.shortage', {}, 'Restordre / manko')} ({shortageCount})
      </div>
    ) : null;
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 150,      // 150ms long-press for mobile - fast response
        tolerance: 5,     // 5px movement tolerance
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = lines.findIndex((line) => line.pListLineNo === active.id);
      const newIndex = lines.findIndex((line) => line.pListLineNo === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(oldIndex, newIndex);
      }
    }
  };

  // If drag-and-drop is disabled, render simple list without DnD context
  if (!enableDragAndDrop) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6">
          <div className="space-y-2 transition-all duration-500 ease-in-out">
            {lines.map((line, index) => (
              <React.Fragment key={line.pListLineNo}>
                {shortageHeader(index)}
                <PickingLineItem
                line={line}
                index={index}
                isSelected={index === currentLineIndex}
                isActive={index === activeLineIndex}

                pallets={pallets}
                editingLocation={editingLocation}
                editingQuantity={editingQuantity}
                newLocation={newLocation}
                newQuantity={newQuantity}
                showPalletInfo={showPalletInfo}
                onLineClick={onLineClick}
                onLocationEdit={onLocationEdit}
                onQuantityEdit={onQuantityEdit}
                onStartLocationEdit={onStartLocationEdit}
                onStartQuantityEdit={onStartQuantityEdit}
                onLocationChange={onLocationChange}
                onQuantityChange={onQuantityChange}
                onSplitLine={onSplitLine}
                onPickLine={onPickLine}
                onUndoPicking={onUndoPicking}
                onShowPalletInfo={onShowPalletInfo}
                onMoveToEnd={onMoveToEnd}
                />
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Render with drag-and-drop enabled
  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Picking Lines */}
      <div className="p-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={lines.map((line) => line.pListLineNo)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2 transition-all duration-500 ease-in-out">
              {lines.map((line, index) => (
                <React.Fragment key={line.pListLineNo}>
                  {shortageHeader(index)}
                  <SortablePickingLineItem
                  line={line}
                  index={index}
                  isSelected={index === currentLineIndex}
                  isActive={index === activeLineIndex}
  
                  pallets={pallets}
                  editingLocation={editingLocation}
                  editingQuantity={editingQuantity}
                  newLocation={newLocation}
                  newQuantity={newQuantity}
                  showPalletInfo={showPalletInfo}
                  onLineClick={onLineClick}
                  onLocationEdit={onLocationEdit}
                  onQuantityEdit={onQuantityEdit}
                  onStartLocationEdit={onStartLocationEdit}
                  onStartQuantityEdit={onStartQuantityEdit}
                  onLocationChange={onLocationChange}
                  onQuantityChange={onQuantityChange}
                  onSplitLine={onSplitLine}
                  onPickLine={onPickLine}
                  onUndoPicking={onUndoPicking}
                  onShowPalletInfo={onShowPalletInfo}
                  onMoveToEnd={onMoveToEnd}
                  />
                </React.Fragment>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};